import { useCallback, useEffect, useState } from "react";
import { formatPesos, parsePesos, MoneyInputError } from "../money";
import type { BooksApi, Category, LineStatus, PlanBody } from "./api";

/**
 * What is still to spend, and how much capital that needs
 * (T034, User Story 5, FR-136).
 *
 * The figure at the top decides the next capital call, so the screen is careful
 * about what it does not know: an unpriced line is shown as unpriced and left
 * out of the totals, and the totals say plainly that they are a floor. A total
 * that quietly assumed an unpriced renovation was free would be worse than no
 * total at all.
 */

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_LABEL: Record<LineStatus, string> = {
  open: "still to spend",
  closed: "closed",
  unpriced: "not priced yet",
};

export interface PlanProps {
  api: BooksApi;
  version: number;
  onChanged(): void;
}

export function Plan({ api, version, onChanged }: PlanProps) {
  const [body, setBody] = useState<PlanBody | null>(null);
  const [form, setForm] = useState<"line" | "commitment" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBody(await api.plan());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the plan");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const done = (message: string) => {
    setForm(null);
    setNotice(message);
    onChanged();
    void load();
  };

  const changeStatus = async (id: string, status: LineStatus) => {
    try {
      await api.setLineStatus(id, { status, note: null });
      done(status === "closed" ? "Line closed." : "Line reopened.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not change it");
    }
  };

  if (form === "line") {
    return <BudgetLineForm api={api} onDone={done} onCancel={() => setForm(null)} />;
  }
  if (form === "commitment") {
    return <CommitmentForm api={api} onDone={done} onCancel={() => setForm(null)} />;
  }

  return (
    <section className="books__panel">
      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="books__notice" role="status">
          {notice}
        </p>
      )}

      {!body ? (
        <p className="books__hint">Loading…</p>
      ) : (
        <>
          <p className="books__headline">
            <span>Capital still needed</span>
            <strong>₱{formatPesos(body.capitalStillNeeded)}</strong>
            <span className="books__row-meta">
              ₱{formatPesos(body.openRemaining)} still to spend, ₱{formatPesos(body.moneyAvailable)} in
              hand
              {body.totalsAreAFloor && " — at least, while something is still unpriced"}
            </span>
          </p>

          {body.totalsAreAFloor && (
            <p className="books__hint">
              {body.unpricedLines} line{body.unpricedLines === 1 ? " has" : "s have"} no price yet, so
              every total here is a floor, not the real figure.
            </p>
          )}

          <h3 className="books__subhead">Planned spending</h3>
          <ul className="books__list">
            {body.lines.map((line) => (
              <li key={line.id} className="books__row">
                <span className="books__row-name">
                  {line.name}
                  {line.status !== "open" && <span className="books__chip">{STATUS_LABEL[line.status]}</span>}
                  {line.overrun > 0 && <span className="books__chip">over</span>}
                </span>
                <span className="books__figure">
                  {line.estimate === null ? "—" : `₱${formatPesos(line.remaining)}`}
                </span>
                <button
                  type="button"
                  className="books__action"
                  onClick={() => void changeStatus(line.id, line.status === "closed" ? "open" : "closed")}
                >
                  {line.status === "closed" ? "Reopen" : "Close"}
                </button>
                <span className="books__row-meta">
                  {line.estimate === null
                    ? "Nobody has priced this yet."
                    : `₱${formatPesos(line.spent)} spent of ₱${formatPesos(line.estimate)}`}
                  {line.overrun > 0 && ` · ₱${formatPesos(line.overrun)} over the estimate`}
                </span>
              </li>
            ))}
          </ul>

          <p className="books__total">
            Spent to open <strong>₱{formatPesos(body.setupSpend)}</strong> · projected total ₱
            {formatPesos(body.projectedTotal)}
          </p>

          <h3 className="books__subhead">Every month</h3>
          {body.commitments.length === 0 ? (
            <p className="books__hint">
              No recurring costs recorded. Adding rent and wages here makes break-even real.
            </p>
          ) : (
            <ul className="books__list">
              {body.commitments.map((commitment) => (
                <li key={commitment.id} className="books__row">
                  <span className="books__row-name">
                    {commitment.name}
                    {commitment.isEstimate && <span className="books__chip">estimate</span>}
                    {commitment.endsOn !== null && <span className="books__chip">ended</span>}
                  </span>
                  <span className="books__figure">₱{formatPesos(commitment.monthlyAmount)}</span>
                  <span className="books__row-meta">
                    from {commitment.startsOn}
                    {commitment.endsOn !== null && ` to ${commitment.endsOn}`}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <p className="books__total">
            Committed every month <strong>₱{formatPesos(body.monthlyCommitted)}</strong>
          </p>

          <div className="books__buttons">
            <button type="button" className="books__action" onClick={() => setForm("commitment")}>
              Add a monthly cost
            </button>
            <button type="button" className="books__action is-primary" onClick={() => setForm("line")}>
              Add a line
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

interface FormProps {
  api: BooksApi;
  onDone(message: string): void;
  onCancel(): void;
}

function BudgetLineForm({ api, onDone, onCancel }: FormProps) {
  const [name, setName] = useState("");
  const [estimate, setEstimate] = useState("");
  const [unpriced, setUnpriced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (name.trim() === "") {
      setError("Say what the money is for.");
      return;
    }

    let centavos: number | null = null;
    if (!unpriced) {
      try {
        centavos = parsePesos(estimate);
      } catch (caught) {
        setError(caught instanceof MoneyInputError ? caught.message : "That is not an amount.");
        return;
      }
      if (centavos === 0) {
        setError("Enter an estimate, or say it is not priced yet.");
        return;
      }
    }

    setBusy(true);
    try {
      await api.addBudgetLine({
        name: name.trim(),
        estimate: centavos,
        status: unpriced ? "unpriced" : "open",
        note: null,
      });
      onDone(unpriced ? `${name.trim()} added, still to be priced.` : `${name.trim()} added.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not add it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="books__panel">
      <h3 className="books__subhead">Add a planned line</h3>

      <label className="books__field">
        <span>What it is for</span>
        <input value={name} onChange={(event) => setName(event.target.value)} maxLength={200} autoFocus />
      </label>

      <label className="books__field">
        <span>Estimate (pesos)</span>
        <input
          value={estimate}
          onChange={(event) => setEstimate(event.target.value)}
          inputMode="decimal"
          disabled={unpriced}
        />
      </label>

      <label className="books__check">
        <input type="checkbox" checked={unpriced} onChange={(event) => setUnpriced(event.target.checked)} />
        <span>Nobody has priced this yet — leave it out of the totals</span>
      </label>

      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="books__action is-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Saving…" : "Add"}
        </button>
      </div>
    </section>
  );
}

function CommitmentForm({ api, onDone, onCancel }: FormProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [startsOn, setStartsOn] = useState(today());
  const [isEstimate, setIsEstimate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void api
      .categories()
      .then((body) => {
        if (!live) return;
        // Only running costs recur every month; setup spending does not.
        const running = body.categories.filter((category) => category.kind === "running");
        setCategories(running);
        setCategoryId((current) => current || running[0]?.id || "");
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api]);

  const submit = async () => {
    setError(null);
    if (name.trim() === "") {
      setError("Give it a name.");
      return;
    }

    let centavos: number;
    try {
      centavos = parsePesos(amount);
    } catch (caught) {
      setError(caught instanceof MoneyInputError ? caught.message : "That is not an amount.");
      return;
    }
    if (centavos === 0) {
      setError("Enter what it costs each month.");
      return;
    }

    setBusy(true);
    try {
      await api.addCommitment({
        name: name.trim(),
        categoryId,
        monthlyAmount: centavos,
        isEstimate,
        startsOn,
        replaces: null,
      });
      onDone(`${name.trim()} added at ₱${formatPesos(centavos)} a month.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not add it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="books__panel">
      <h3 className="books__subhead">Add a monthly cost</h3>
      <p className="books__hint">
        It counts toward profit and break-even from its start date, whether or not the bill has
        arrived — so an unpaid month never flatters the figures.
      </p>

      <label className="books__field">
        <span>What it is</span>
        <input value={name} onChange={(event) => setName(event.target.value)} maxLength={200} autoFocus />
      </label>

      <div className="books__pickers">
        <label className="books__field">
          <span>Kind</span>
          <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>

        <label className="books__field">
          <span>Every month (pesos)</span>
          <input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode="decimal" />
        </label>
      </div>

      <label className="books__field">
        <span>From</span>
        <input type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} />
      </label>

      <label className="books__check">
        <input type="checkbox" checked={isEstimate} onChange={(event) => setIsEstimate(event.target.checked)} />
        <span>This is an estimate until a real bill arrives</span>
      </label>

      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="books__action is-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Saving…" : "Add"}
        </button>
      </div>
    </section>
  );
}
